import { Loader } from '@progress/kendo-react-indicators'
import { Tooltip } from '@progress/kendo-react-tooltip'
import { useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useBeforeUnload, useToggle } from 'react-use'

import { HStack } from '~/components/elements/HStack'
import { StyledButton } from '~/components/elements/StyledButton'
import { StyledTextArea } from '~/components/elements/StyledTextArea'
import { VStack } from '~/components/elements/VStack'
import {
  SaveDocumentImageDialog,
  type SaveDocumentImageDialogProps,
} from '~/components/features/SaveDocumentImageDialog'
import { useBrandAccountAiEnabledQuery } from '~/hooks/useBrandAccountAiEnabledQuery'
import { useCommonQueryCurrentBrandAccount } from '~/hooks/useCommonQuery'
import { exists } from '~/utils'

import styles from './AnalysisCommentBlock.css'
import { dirtyCommentCountAtom } from './dirtyCommentCountAtom'

type AnalysisCommentBlockProps = {
  isGenerating?: boolean
  onAutoGenerate?: () => void
  onCommentChange: (comment: string) => void
  // Baseline = last restored/saved value. dirty := value !== savedComment.
  savedComment?: string
  saveDocumentImage?: Omit<SaveDocumentImageDialogProps, 'onClose'>
  value: string
}

export const AnalysisCommentBlock = ({
  isGenerating = false,
  onAutoGenerate,
  onCommentChange,
  savedComment = '',
  saveDocumentImage,
  value,
}: AnalysisCommentBlockProps) => {
  const { t } = useTranslation()
  const [isSaveDialogVisible, toggleSaveDialog] = useToggle(false)
  const setDirtyCount = useSetAtom(dirtyCommentCountAtom)
  const isDirty = value !== savedComment
  const wasDirtyRef = React.useRef(false)
  // A-6: when the BrandAccount has AI off, the auto-generate button is greyed
  // with mouseover guidance instead of letting the click hit the server gate.
  const brandAccountId = useCommonQueryCurrentBrandAccount()?.id
  const { data: aiEnabledData, isPending } =
    useBrandAccountAiEnabledQuery(brandAccountId)
  const aiDisabled = aiEnabledData?.brandAccount?.aiEnabled === false

  React.useEffect(() => {
    if (isDirty && !wasDirtyRef.current) {
      setDirtyCount((c) => c + 1)
    } else if (!isDirty && wasDirtyRef.current) {
      setDirtyCount((c) => c - 1)
    }
    wasDirtyRef.current = isDirty
  }, [isDirty, setDirtyCount])

  React.useEffect(
    () => () => {
      if (wasDirtyRef.current) {
        setDirtyCount((c) => c - 1)
      }
    },
    [setDirtyCount],
  )

  const handleAutoGenerate = React.useCallback(() => {
    if (aiDisabled) {
      return
    }
    if (
      value.trim() !== '' &&
      !window.confirm(t('confirm_overwrite_ai_comment'))
    ) {
      return
    }
    onAutoGenerate?.()
  }, [aiDisabled, onAutoGenerate, value, t])

  useBeforeUnload(
    isDirty,
    t('edited_content_has_not_been_saved_do_you_still_want_to_leave'),
  )

  return (
    <>
      <HStack className={styles.root} spacing={8}>
        <StyledTextArea
          disabled={isGenerating}
          placeholder={t('please_enter_a_comment')}
          rows={5}
          style={{ flex: 1 }}
          value={value}
          onValueChange={onCommentChange}
        />
        <VStack spacing={4}>
          {exists(onAutoGenerate) && (
            <VStack spacing={1}>
              <Tooltip anchorElement="target" openDelay={0} position="left">
                <span
                  style={{ display: 'inline-block' }}
                  title={aiDisabled ? t('ai_disabled_message') : undefined}
                >
                  <StyledButton
                    disabled={aiDisabled || isGenerating || isPending}
                    onClick={handleAutoGenerate}
                  >
                    {t('auto_generate_ai_comment')}
                  </StyledButton>
                </span>
              </Tooltip>
              {isGenerating && <Loader size="small" type="infinite-spinner" />}
            </VStack>
          )}
          {exists(saveDocumentImage) && (
            <StyledButton onClick={toggleSaveDialog}>
              {t('save_table_chart_with_comment')}
            </StyledButton>
          )}
        </VStack>
      </HStack>
      {exists(saveDocumentImage) && isSaveDialogVisible && (
        <SaveDocumentImageDialog
          {...saveDocumentImage}
          onClose={toggleSaveDialog}
        />
      )}
    </>
  )
}
